import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api, { questionAPI } from '../services/api';
import toast from 'react-hot-toast';

const SkillAssessmentsByDifficulty = ({ onAssessmentStateChange, resumeAssessmentId, onResumeHandled }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('available'); // available, history
  const [assessmentsByDifficulty, setAssessmentsByDifficulty] = useState({
    beginner: [],
    intermediate: [],
    advanced: []
  });
  const [completedSkills, setCompletedSkills] = useState({
    beginner: [],
    intermediate: [],
    advanced: []
  });
  const [assessmentHistory, setAssessmentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [takingAssessment, setTakingAssessment] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [assessmentStartTime, setAssessmentStartTime] = useState(null);
  const [showResults, setShowResults] = useState(null);
  const [selectedAttemptId, setSelectedAttemptId] = useState(null);
  const [attemptDetails, setAttemptDetails] = useState(null);
  const [attemptDetailsLoading, setAttemptDetailsLoading] = useState(false);
  const [scrollPositions, setScrollPositions] = useState({
    beginner: 0,
    intermediate: 0,
    advanced: 0
  });
  const progressSaveTimer = useRef(null);

  useEffect(() => {
    fetchAssessmentsByDifficulty();
    fetchAssessmentHistory();
  }, []);

  // Notify parent about assessment state
  useEffect(() => {
    if (onAssessmentStateChange) {
      onAssessmentStateChange(takingAssessment !== null);
    }
  }, [takingAssessment, onAssessmentStateChange]);

  // Timer effect
  useEffect(() => {
    if (takingAssessment && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleSubmitAssessment();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [takingAssessment, timeRemaining]);

  const fetchAssessmentsByDifficulty = async () => {
    try {
      setLoading(true);
      
      // Get candidate's skills from their profile
      const candidateResponse = await api.get('/candidates/me');
      const candidate = candidateResponse.data;
      
      if (!candidate.skills || candidate.skills.length === 0) {
        toast.error('Please add skills to your profile to see available assessments');
        setAssessmentsByDifficulty({ beginner: [], intermediate: [], advanced: [] });
        return;
      }
      
      // Get assessments grouped by difficulty
      const response = await questionAPI.getAssessmentsByDifficulty(user.id);
      setAssessmentsByDifficulty(response.data.assessments_by_difficulty);
      setCompletedSkills(response.data.completed_skills);
      
    } catch (error) {
      console.error('Error fetching assessments:', error);
      toast.error('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssessmentHistory = async () => {
    try {
      const response = await questionAPI.getAssessmentHistory(user.id);
      setAssessmentHistory(response.data.assessments || []);
    } catch (error) {
      console.error('Error fetching assessment history:', error);
    }
  };

  const handleViewAttemptDetails = async (attempt) => {
    const attemptId = attempt?.attempt_id || attempt?.id;
    if (!attemptId) {
      toast.error('Unable to load this assessment attempt.');
      return;
    }

    setSelectedAttemptId(attemptId);
    setAttemptDetails(null);
    setAttemptDetailsLoading(true);

    try {
      const response = await questionAPI.getAssessmentAttemptDetails(attemptId);
      setAttemptDetails(response.data);
    } catch (error) {
      console.error('Error fetching attempt details:', error);
      toast.error('Failed to load assessment results. Please try again.');
      setSelectedAttemptId(null);
      setAttemptDetails(null);
    } finally {
      setAttemptDetailsLoading(false);
    }
  };

  const handleCloseAttemptDetails = () => {
    setSelectedAttemptId(null);
    setAttemptDetails(null);
    setAttemptDetailsLoading(false);
  };

  const handleStartAssessment = async (assessment) => {
    try {
      toast.loading(assessment?.resuming ? 'Resuming assessment...' : 'Starting assessment...', { id: 'starting' });
      
      const response = await questionAPI.startAssessment(assessment.id);
      const assessmentData = response.data;
      
      setTakingAssessment({
        ...assessment,
        id: assessmentData.assessment_id || assessment.id,
        questions: assessmentData.questions,
        sessionId: assessmentData.session_id,
        duration_minutes: assessmentData.duration_minutes || assessment.duration_minutes,
      });
      if (assessmentData.resumed && assessmentData.answers) {
        setAnswers(assessmentData.answers || {});
        setCurrentQuestion(assessmentData.current_question_index || 0);
        const remaining = Number.isFinite(assessmentData.remaining_seconds)
          ? assessmentData.remaining_seconds
          : (assessmentData.duration_minutes || assessment.duration_minutes || 0) * 60;
        setTimeRemaining(remaining);
        toast.success('Continuing your assessment', { id: 'starting' });
      } else {
        setCurrentQuestion(0);
        setAnswers({});
        setTimeRemaining((assessmentData.duration_minutes || assessment.duration_minutes || 0) * 60);
        toast.success('Assessment started! Good luck!', { id: 'starting' });
      }
      setAssessmentStartTime(Date.now());
    } catch (error) {
      console.error('Error starting assessment:', error);
      const message = error?.response?.data?.detail || 'Failed to start assessment';
      toast.error(message, { id: 'starting' });
    }
  };

  useEffect(() => {
    if (!resumeAssessmentId) return undefined;
    handleStartAssessment({ id: resumeAssessmentId, resuming: true });
    if (onResumeHandled) onResumeHandled();
    return undefined;
  }, [resumeAssessmentId]);

  const handleAnswerSelect = (questionId, answerIndex) => {
    setAnswers(prev => {
      const next = {
        ...prev,
        [questionId]: answerIndex
      };
      if (takingAssessment?.id) {
        if (progressSaveTimer.current) clearTimeout(progressSaveTimer.current);
        progressSaveTimer.current = setTimeout(() => {
          questionAPI.saveAssessmentProgress(takingAssessment.id, next).catch(() => {});
        }, 400);
      }
      return next;
    });
  };

  const handleNextQuestion = () => {
    if (currentQuestion < takingAssessment.questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmitAssessment = async () => {
    if (Object.keys(answers).length < takingAssessment.questions.length) {
      if (!window.confirm('You haven\'t answered all questions. Submit anyway?')) {
        return;
      }
    }

    try {
      toast.loading('Submitting assessment...', { id: 'submitting' });
      
      // Calculate time taken
      const timeTaken = assessmentStartTime ? Math.floor((Date.now() - assessmentStartTime) / 1000) : 0;
      
      // Submit answers to the API
      const response = await questionAPI.submitAssessment(takingAssessment.id, answers);
      const results = response.data;
      
      // Add time taken to results
      results.timeTaken = timeTaken;
      
      setShowResults(results);
      setTakingAssessment(null);
      setTimeRemaining(null);
      setAssessmentStartTime(null);
      
      const passed = results.score >= takingAssessment.passing_score;
      toast.success(passed ? 'Congratulations! You passed!' : 'Assessment complete. Keep learning!', { id: 'submitting' });
      
      // Refresh history and available assessments
      fetchAssessmentHistory();
      fetchAssessmentsByDifficulty();
    } catch (error) {
      console.error('Error submitting assessment:', error);
      toast.error('Failed to submit assessment', { id: 'submitting' });
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDifficultyIcon = (difficulty) => {
    switch (difficulty) {
      case 'beginner':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'intermediate':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
      case 'advanced':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const formatDifficultyLevel = (difficulty) => {
    if (!difficulty) return 'Unknown';
    return difficulty.charAt(0).toUpperCase() + difficulty.slice(1);
  };

  // Carousel navigation handlers
  const scrollCarousel = (difficulty, direction) => {
    const container = document.getElementById(`carousel-${difficulty}`);
    if (!container) return;

    const cardWidth = 256; // Approximate card width including gap (w-64 = 256px + gap)
    const scrollAmount = cardWidth * 2; // Scroll 2 cards at a time
    const currentScroll = container.scrollLeft;

    const newPosition = direction === 'left' 
      ? Math.max(0, currentScroll - scrollAmount)
      : currentScroll + scrollAmount;

    container.scrollTo({
      left: newPosition,
      behavior: 'smooth'
    });
  };

  // Assessment Carousel Component
  const AssessmentCarousel = ({ difficulty, assessments, onStart }) => {
    const containerRef = React.useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    const checkScrollability = () => {
      if (containerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
      }
    };

    useEffect(() => {
      checkScrollability();
      const container = containerRef.current;
      if (container) {
        container.addEventListener('scroll', checkScrollability);
        window.addEventListener('resize', checkScrollability);
        return () => {
          container.removeEventListener('scroll', checkScrollability);
          window.removeEventListener('resize', checkScrollability);
        };
      }
    }, [assessments]);

    if (assessments.length === 0) {
      return (
        <p className="text-gray-500 text-center py-4">No {difficulty} assessments available</p>
      );
    }

    return (
      <div className="relative">
        {/* Left Arrow */}
        {canScrollLeft && (
          <button
            onClick={() => scrollCarousel(difficulty, 'left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full shadow-lg p-2 hover:bg-gray-50 transition-colors border border-gray-200"
            aria-label="Scroll left"
          >
            <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Carousel Container */}
        <div
          id={`carousel-${difficulty}`}
          ref={containerRef}
          className="flex gap-4 overflow-x-auto pb-4 scroll-smooth"
          style={{ 
            scrollbarWidth: 'none', 
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch'
          }}
          onScroll={checkScrollability}
        >
          {assessments.map(assessment => (
            <div key={assessment.id} className="flex-shrink-0 w-64 h-full">
              <AssessmentCard 
                assessment={assessment} 
                onStart={onStart}
              />
            </div>
          ))}
        </div>

        {/* Right Arrow */}
        {canScrollRight && (
          <button
            onClick={() => scrollCarousel(difficulty, 'right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full shadow-lg p-2 hover:bg-gray-50 transition-colors border border-gray-200"
            aria-label="Scroll right"
          >
            <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

        <style>{`
          #carousel-${difficulty}::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </div>
    );
  };

  // Results Modal
  if (showResults) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <div className={`w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center ${
            showResults.passed ? 'bg-green-100' : 'bg-yellow-100'
          }`}>
            {showResults.passed ? (
              <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="w-12 h-12 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
          </div>
          
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {showResults.passed ? 'Congratulations!' : 'Assessment Complete'}
          </h2>
          <p className="text-gray-600 mb-6">{showResults.title}</p>
          
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <div className="text-6xl font-bold mb-2" style={{ color: showResults.passed ? '#10b981' : '#f59e0b' }}>
              {showResults.score}%
            </div>
            <p className="text-gray-600">Your Score</p>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">{showResults.correct_answers || 0}</p>
              <p className="text-sm text-gray-600">Correct</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">{(showResults.total_questions || 0) - (showResults.correct_answers || 0)}</p>
              <p className="text-sm text-gray-600">Incorrect</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">{showResults.timeTaken ? formatTime(showResults.timeTaken) : 'N/A'}</p>
              <p className="text-sm text-gray-600">Time Taken</p>
            </div>
          </div>
          
          <button
            onClick={() => {
              setShowResults(null);
              setActiveTab('history');
            }}
            className="w-full btn-primary"
          >
            View All Assessments
          </button>
        </div>
      </div>
    );
  }

  // Helper function to clean question text - remove skill name if it appears at the start
  const cleanQuestionText = (questionText, skillName) => {
    if (!questionText || !skillName) return questionText;
    
    // Remove skill name if it appears at the start of the question (case insensitive)
    const skillPattern = new RegExp(`^${skillName}\\s*\\n?`, 'i');
    return questionText.replace(skillPattern, '').trim();
  };

  // Assessment Taking Interface
  if (takingAssessment) {
    const question = takingAssessment.questions[currentQuestion];
    const progress = ((currentQuestion + 1) / takingAssessment.questions.length) * 100;
    
    // Clean question text to remove skill name if present
    const cleanedQuestionText = cleanQuestionText(
      question?.question, 
      takingAssessment.skill_name
    );

    const handleCancelAssessment = () => {
      if (window.confirm('Are you sure you want to cancel this assessment? Your progress will not be saved.')) {
        setTakingAssessment(null);
        setCurrentQuestion(0);
        setAnswers({});
        setTimeRemaining(null);
        setAssessmentStartTime(null);
        toast.success('Assessment cancelled');
      }
    };

    return (
      <div className="w-full">
        {/* Header with Cancel Button */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex justify-between items-center mb-3">
            <div className="flex-1">
              <button
                onClick={handleCancelAssessment}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-red-600 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="font-medium">Cancel</span>
              </button>
            </div>
            <div className="flex-1 text-center">
              <h2 className="text-lg font-bold text-gray-900">
                {question?.category || takingAssessment.category || takingAssessment.skill_name || 'Assessment'} - {takingAssessment.difficulty_level ? takingAssessment.difficulty_level.charAt(0).toUpperCase() + takingAssessment.difficulty_level.slice(1) : ''} Level
              </h2>
              <p className="text-sm text-gray-600">Question {currentQuestion + 1} of {takingAssessment.questions.length}</p>
            </div>
            <div className="flex-1 text-right">
              <div className={`text-2xl font-bold ${timeRemaining < 300 ? 'text-red-600' : 'text-primary-600'}`}>
                {formatTime(timeRemaining)}
              </div>
              <p className="text-xs text-gray-600">Time Remaining</p>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div className="bg-primary-600 h-1.5 rounded-full transition-all" style={{ width: `${progress}%` }}></div>
          </div>
        </div>

        {/* Main Content - Side by Side Layout */}
        <div className="grid grid-cols-3 gap-4">
          {/* Question Card - Takes 2 columns */}
          <div className="col-span-2 bg-white rounded-xl shadow-sm">
            <div className="p-4">
              <div className="mb-3">
                <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full">
                  {question.category || 'General'}
                </span>
              </div>
              
              <h3 className="text-base font-semibold text-gray-900 mb-4">{cleanedQuestionText || 'Question not available'}</h3>
              
              {/* Options */}
              <div className="space-y-2 mb-4">
                {(question?.options || []).map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(question.id, index)}
                    className={`w-full text-left p-2 rounded-lg border-2 transition-all ${
                      answers[question.id] === index
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center mr-2 ${
                        answers[question.id] === index
                          ? 'border-primary-600 bg-primary-600'
                          : 'border-gray-300'
                      }`}>
                        {answers[question.id] === index && (
                          <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="flex-1 text-sm">{option}</span>
                    </div>
                  </button>
                ))}
              </div>
              
              {/* Navigation */}
              <div className="flex justify-between pt-3 border-t border-gray-200">
                <button
                  onClick={handlePreviousQuestion}
                  disabled={currentQuestion === 0}
                  className="px-4 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  ← Previous
                </button>
                
                {currentQuestion < takingAssessment.questions.length - 1 ? (
                  <button
                    onClick={handleNextQuestion}
                    className="px-4 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
                  >
                    Next →
                  </button>
                ) : (
                  <button
                    onClick={handleSubmitAssessment}
                    className="px-4 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                  >
                    Submit Assessment
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Question Navigator - Takes 1 column on the right */}
          <div className="col-span-1 bg-white rounded-xl shadow-sm">
            <div className="p-4">
              <h4 className="font-semibold text-sm text-gray-900 mb-3">Question Navigator</h4>
              <div className="grid grid-cols-5 gap-1.5">
                {takingAssessment.questions.map((q, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentQuestion(index)}
                    className={`w-9 h-9 rounded-lg font-medium text-sm transition-all ${
                      index === currentQuestion
                        ? 'bg-primary-600 text-white scale-110'
                        : answers[q.id] !== undefined
                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="space-y-1.5 text-xs">
                  <div className="flex items-center">
                    <div className="w-2.5 h-2.5 rounded bg-primary-600 mr-2"></div>
                    <span className="text-gray-600">Current</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2.5 h-2.5 rounded bg-green-100 mr-2"></div>
                    <span className="text-gray-600">Answered</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2.5 h-2.5 rounded bg-gray-100 mr-2"></div>
                    <span className="text-gray-600">Unanswered</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Assessment List View
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('available')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'available'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Available Tests
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'history'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            My History
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : activeTab === 'available' ? (
        <div className="space-y-8">
          {/* Beginner Level */}
          <div className="ui-card overflow-hidden">
            <div className="bg-green-50 px-6 py-4 border-b border-green-200">
              <div className="flex items-center">
                <div className="flex items-center text-green-800">
                  {getDifficultyIcon('beginner')}
                  <h3 className="text-lg font-semibold ml-2">Beginner Level</h3>
                </div>
                <div className="ml-auto text-sm text-green-600">
                  {assessmentsByDifficulty.beginner.length} assessments available
                </div>
              </div>
            </div>
            <div className="p-6">
              <AssessmentCarousel 
                difficulty="beginner"
                assessments={assessmentsByDifficulty.beginner}
                onStart={handleStartAssessment}
              />
            </div>
          </div>

          {/* Intermediate Level */}
          <div className="ui-card overflow-hidden">
            <div className="bg-yellow-50 px-6 py-4 border-b border-yellow-200">
              <div className="flex items-center">
                <div className="flex items-center text-yellow-800">
                  {getDifficultyIcon('intermediate')}
                  <h3 className="text-lg font-semibold ml-2">Intermediate Level</h3>
                </div>
                <div className="ml-auto text-sm text-yellow-600">
                  {assessmentsByDifficulty.intermediate.length} assessments available
                </div>
              </div>
            </div>
            <div className="p-6">
              <AssessmentCarousel 
                difficulty="intermediate"
                assessments={assessmentsByDifficulty.intermediate}
                onStart={handleStartAssessment}
              />
            </div>
          </div>

          {/* Advanced Level */}
          <div className="ui-card overflow-hidden">
            <div className="bg-red-50 px-6 py-4 border-b border-red-200">
              <div className="flex items-center">
                <div className="flex items-center text-red-800">
                  {getDifficultyIcon('advanced')}
                  <h3 className="text-lg font-semibold ml-2">Advanced Level</h3>
                </div>
                <div className="ml-auto text-sm text-red-600">
                  {assessmentsByDifficulty.advanced.length} assessments available
                </div>
              </div>
            </div>
            <div className="p-6">
              <AssessmentCarousel 
                difficulty="advanced"
                assessments={assessmentsByDifficulty.advanced}
                onStart={handleStartAssessment}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="ui-card overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assessment</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {assessmentHistory.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    No assessment history available yet. Complete an assessment to see your results here.
                  </td>
                </tr>
              ) : (
                assessmentHistory.map(attempt => (
                  <tr key={attempt.attempt_id || attempt.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-semibold text-gray-900">{attempt.title}</p>
                      <p className="text-sm text-gray-600">{attempt.skill_name}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(attempt.difficulty_level)}`}>
                      {formatDifficultyLevel(attempt.difficulty_level)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-2xl font-bold" style={{ color: attempt.passed ? '#10b981' : '#f59e0b' }}>
                      {attempt.score}%
                    </div>
                    <p className="text-xs text-gray-500">{attempt.correct_answers}/{attempt.total_questions} correct</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      attempt.passed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {attempt.passed ? 'Passed' : 'Failed'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(attempt.completed_at)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {attempt.answers_available ? (
                      <button
                        onClick={() => handleViewAttemptDetails(attempt)}
                        className="text-primary-600 hover:text-primary-700 font-medium text-sm"
                      >
                        View Details
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400 italic">Details unavailable</span>
                    )}
                  </td>
                  </tr>
              ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {selectedAttemptId && (
        <AssessmentAttemptDetailsModal
          data={attemptDetails}
          isLoading={attemptDetailsLoading}
          onClose={handleCloseAttemptDetails}
        />
      )}
    </div>
  );
};

const AssessmentAttemptDetailsModal = ({ data, isLoading, onClose }) => {
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const normalizeToIndex = (value) => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
      return parseInt(value, 10);
    }
    return null;
  };

  const normalizeToText = (value) => {
    if (typeof value === 'string' && value.trim() !== '' && !/^\d+$/.test(value.trim())) {
      return value.trim().toLowerCase();
    }
    return null;
  };

  /** Only questions the candidate got wrong (omit questions answered correctly). */
  const questionIsIncorrect = (question) => {
    if (question.is_correct === true) return false;
    if (question.is_correct === false) return true;
    const selectedIndex = normalizeToIndex(question.selected_answer);
    const correctIndex = normalizeToIndex(question.correct_answer);
    const selectedText = normalizeToText(question.selected_answer_label || question.selected_answer);
    const correctText = normalizeToText(question.correct_answer_label || question.correct_answer);
    if (selectedIndex !== null && correctIndex !== null) return selectedIndex !== correctIndex;
    if (selectedText && correctText) return selectedText !== correctText;
    return false;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50 px-4">
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              {data?.assessment_title || 'Assessment Results'}
            </h3>
            {data && (
              <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-600">
                <span className="px-2 py-0.5 bg-primary-50 text-primary-700 rounded-full font-medium">
                  {data.skill_name}
                </span>
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full font-medium">
                  Difficulty: {data.difficulty_level ? data.difficulty_level.charAt(0).toUpperCase() + data.difficulty_level.slice(1) : 'N/A'}
                </span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Close results"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-24 space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            <p className="text-sm text-gray-500">Loading assessment details...</p>
          </div>
        ) : !data ? (
          <div className="py-16 px-6 text-center text-gray-500">
            No result details are available for this attempt.
          </div>
        ) : (
          <div className="max-h-[70vh] overflow-y-auto px-6 pb-6">
            <div className="grid gap-4 md:grid-cols-4 py-6">
              <div className="col-span-2 md:col-span-1 bg-primary-50 border border-primary-100 rounded-xl p-4">
                <p className="text-sm text-primary-600 font-medium mb-1">Score</p>
                <p className="text-3xl font-bold text-primary-700">{data.score ?? 'N/A'}%</p>
                <p className="text-xs text-primary-500 mt-1">{data.correct_answers}/{data.total_questions} correct</p>
              </div>
              <div className="col-span-2 md:col-span-1 bg-green-50 border border-green-100 rounded-xl p-4">
                <p className="text-sm text-green-700 font-medium mb-1">Status</p>
                <p className={`text-lg font-semibold ${data.passed ? 'text-green-700' : 'text-yellow-700'}`}>
                  {data.passed ? 'Passed' : 'Failed'}
                </p>
                <p className="text-xs text-green-600 mt-1 capitalize">Passing Score: {data.passing_score ?? 'N/A'}%</p>
              </div>
              <div className="col-span-2 md:col-span-1 bg-gray-50 border border-gray-200 rounded-xl p-4">
                <p className="text-sm text-gray-600 font-medium mb-1">Completed On</p>
                <p className="text-sm text-gray-900">{formatDateTime(data.completed_at)}</p>
                {data.started_at && (
                  <p className="text-xs text-gray-500 mt-1">Started: {formatDateTime(data.started_at)}</p>
                )}
              </div>
              <div className="col-span-2 md:col-span-1 bg-indigo-50 border border-indigo-100 rounded-xl p-4">
                <p className="text-sm text-indigo-600 font-medium mb-1">Time Taken</p>
                <p className="text-lg font-semibold text-indigo-700">
                  {data.time_taken_minutes !== null && data.time_taken_minutes !== undefined
                    ? `${Math.round(data.time_taken_minutes)} min`
                    : 'N/A'}
                </p>
              </div>
            </div>

            <p className="text-sm text-gray-600 -mt-2 mb-2">
              Review shows incorrectly answered questions only. Questions you got right are hidden.
            </p>

            <div className="space-y-4 pb-6">
              {(() => {
                const incorrectOnly = (data.questions || []).filter(questionIsIncorrect);
                if (incorrectOnly.length === 0) {
                  return (
                    <div className="rounded-xl border border-green-100 bg-green-50 px-6 py-10 text-center">
                      <p className="text-lg font-semibold text-green-800">No incorrect answers</p>
                      <p className="mt-2 text-sm text-green-700">
                        Every question in this attempt was answered correctly, or details are unavailable.
                      </p>
                    </div>
                  );
                }
                return incorrectOnly.map(question => {
                const selectedIndex = normalizeToIndex(question.selected_answer);
                const correctIndex = normalizeToIndex(question.correct_answer);
                const selectedText = normalizeToText(question.selected_answer_label || question.selected_answer);
                const correctText = normalizeToText(question.correct_answer_label || question.correct_answer);

                const renderOptionRow = (option, index) => {
                  const optionTextNormalized = option ? option.toString().toLowerCase() : '';
                  const isSelected =
                    selectedIndex === index ||
                    (!!selectedText && optionTextNormalized === selectedText);
                  const isCorrect =
                    correctIndex === index ||
                    (!!correctText && optionTextNormalized === correctText);
                  
                  let optionClass = 'p-3 border rounded-lg flex items-center justify-between transition-colors';
                  if (isCorrect) {
                    optionClass += ' border-green-500 bg-green-50';
                  } else if (isSelected) {
                    optionClass += ' border-red-400 bg-red-50';
                  } else {
                    optionClass += ' border-gray-200 bg-white';
                  }

                  return (
                    <div key={index} className={optionClass}>
                      <div className="flex items-start gap-3">
                        <span className="text-sm font-medium text-gray-500">{String.fromCharCode(65 + index)}.</span>
                        <span className="text-sm text-gray-800">{option}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {isCorrect && (
                          <span className="flex items-center text-xs font-semibold text-green-700">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Correct
                          </span>
                        )}
                        {isSelected && !isCorrect && (
                          <span className="flex items-center text-xs font-semibold text-red-600">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Your Answer
                          </span>
                        )}
                      </div>
                    </div>
                  );
                };

                return (
                  <div key={question.question_id || question.question_number} className="border border-gray-200 rounded-xl p-5 bg-white shadow-sm">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mt-1">
                          {question.question || 'Question text unavailable'}
                        </h4>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {question.category && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                            {question.category}
                          </span>
                        )}
                        {question.difficulty && (
                          <span className="px-2 py-0.5 bg-primary-50 text-primary-600 rounded-full text-xs font-medium capitalize">
                            {question.difficulty}
                          </span>
                        )}
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-semibold flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Incorrect
                        </span>
                      </div>
                    </div>

                    {question.options && question.options.length > 0 ? (
                      <div className="mt-4 space-y-2">
                        {question.options.map((option, index) => renderOptionRow(option, index))}
                      </div>
                    ) : (
                      <div className="mt-4 grid gap-3 md:grid-cols-2">
                        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                          <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Your Answer</p>
                          <p className="text-sm text-gray-800">
                            {question.selected_answer_label || question.selected_answer || 'Not answered'}
                          </p>
                        </div>
                        <div className="border border-green-200 rounded-lg p-3 bg-green-50">
                          <p className="text-xs text-green-600 uppercase font-semibold mb-1">Correct Answer</p>
                          <p className="text-sm text-green-800">
                            {question.correct_answer_label || question.correct_answer || 'N/A'}
                          </p>
                        </div>
                      </div>
                    )}

                    {question.explanation && (
                      <div className="mt-4 bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-800">
                        <p className="font-medium mb-1">Explanation</p>
                        <p className="text-blue-700">{question.explanation}</p>
                      </div>
                    )}
                  </div>
                );
              });
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Assessment Card Component
const AssessmentCard = ({ assessment, onStart }) => {
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDifficultyLevel = (difficulty) => {
    if (!difficulty) return 'Unknown';
    return difficulty.charAt(0).toUpperCase() + difficulty.slice(1);
  };

  return (
    <div className={`bg-white rounded-lg border-2 p-3 transition-all flex flex-col ${
      assessment.can_take 
        ? 'border-gray-200 hover:border-primary-300 hover:shadow-md' 
        : 'border-gray-100 bg-gray-50'
    }`} style={{ height: '260px' }}>
      <div className="flex items-start justify-between mb-2 flex-shrink-0">
        <div className="flex-1 min-w-0 pr-2">
          <h4 
            className="font-semibold text-gray-900 mb-1 text-sm" 
            title={assessment.title}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: '1.3',
              maxHeight: '2.6em'
            }}
          >
            {assessment.title}
          </h4>
          <p 
            className="text-xs text-gray-600 truncate" 
            title={assessment.skill_name}
          >
            {assessment.skill_name}
          </p>
        </div>
        <span className={`px-1.5 py-0.5 text-xs rounded-full flex-shrink-0 ${getDifficultyColor(assessment.difficulty_level)}`}>
          {formatDifficultyLevel(assessment.difficulty_level)}
        </span>
      </div>
      
      <p 
        className="text-gray-600 text-xs mb-2 flex-grow-0" 
        title={assessment.description}
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          lineHeight: '1.3',
          maxHeight: '2.6em',
          minHeight: '2.6em'
        }}
      >
        {assessment.description}
      </p>
      
      <div className="grid grid-cols-2 gap-2 mb-2 text-xs text-gray-600 flex-shrink-0">
        <div className="flex items-center">
          <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {assessment.duration_minutes} mins
        </div>
        <div className="flex items-center">
          <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          {assessment.total_questions} questions
        </div>
      </div>
      
      <div className="border-t pt-2 mt-auto flex-shrink-0">
        <p className="text-xs text-gray-500 mb-1.5">Pass score: {assessment.passing_score}%</p>

        {typeof assessment.attempts_remaining_today === 'number' && (
          <p className="text-xs text-gray-500 mb-1.5">
            Attempts remaining today: <span className="font-semibold">{assessment.attempts_remaining_today}</span>/3
          </p>
        )}
        
        {assessment.is_completed ? (
          <button
            disabled
            className="w-full bg-green-50 text-green-700 border border-green-200 rounded-md text-xs py-1.5 font-medium flex items-center justify-center cursor-default"
          >
            <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Completed
          </button>
        ) : assessment.can_take && (assessment.attempts_remaining_today === undefined || assessment.attempts_remaining_today > 0) ? (
          <button
            onClick={() => onStart(assessment)}
            className="w-full btn-primary text-xs py-1.5"
          >
            Start Assessment
          </button>
        ) : (
          <div className="w-full bg-gray-50 text-gray-500 border border-gray-200 rounded-md text-xs py-1.5 text-center italic min-h-[32px] flex items-center justify-center">
            {assessment.attempts_remaining_today === 0 ? 'Daily attempt limit reached' : (assessment.reason || 'Not available')}
          </div>
        )}
      </div>
    </div>
  );
};

export default SkillAssessmentsByDifficulty;

